import React, { useState, useRef, useEffect } from "react";
import styles from "./dropDown.module.css";
import Label from "../V2/Widgets/Label";
import ErrorLabel from "../V2/Widgets/Label/errorLabel";
import Input from "../V2/Widgets/Input";
import DropdownSvg from "../../public/images/Dropdown.svg";
import DropUpSvg from "../../public/images/DropUp.svg";
import OutsideClickHandler from "react-outside-click-handler";
import { Spinner } from "react-bootstrap";
import { createDropdownOptions } from "./dropdownHelper";

const DropDown = ({
  label,
  name,
  value,
  data = [],
  onChange,
  placeholder = "Select",
  required = false,
  error,
  helperText,
  className,
  inputClassName,
  height = "40px",
  disabled = false,
  isSearchable = true,
  scrollbar = true,
  isMulti = false,
  onOpen,
  loading = false,
  labelKey = "label",
  valueKey = "value",
  isAllValue = false,
  showClearButton = true,
  clearButtonIcon = "×",
  onClear,
  formatValue = (val) => {
    if (!val) return "";
    if (typeof val === "object") val = val.label ?? val.value ?? "";
    if (typeof val !== "string") return val;

    if (val.includes("_")) {
      return val
        .split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }
    if (val === val.toUpperCase()) {
      return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
    }
    return val;
  },
}) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [searched, setSearched] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const inputRef = useRef(null);
    const normalizeText = (text = "") =>
      text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

  useEffect(() => {
    const opts = createDropdownOptions(data, labelKey, valueKey, isAllValue);
    setOptions(opts);
  }, [data, labelKey, valueKey, isAllValue]);

  useEffect(() => {
    const baseOptions = createDropdownOptions(
      data,
      labelKey,
      valueKey,
      isAllValue
    );
    if (searched !== "") {
      const updatedList = baseOptions.filter((item) =>
        normalizeText(formatValue(item.label)).includes(
          normalizeText(searched)
        )
      );
      setOptions(updatedList);
    } else {
      setOptions(baseOptions);
    }
  }, [searched, data, labelKey, valueKey, isAllValue]);

  useEffect(() => {
    if (showDropDown && inputRef.current) inputRef.current.focus();
  }, [showDropDown]);

  useEffect(() => {
    if (isMulti) {
      setSelectedValues(Array.isArray(value) ? value : []);
    }
  }, [value, isMulti]);

  const toggleSelection = (item) => {
    const val = typeof item === "object" ? item.value : item;
    const label = typeof item === "object" ? item.label : item;

    const alreadySelected = selectedValues.some((v) => (v.value ?? v) === val);

    const updated = alreadySelected
      ? selectedValues.filter((v) => (v.value ?? v) !== val)
      : [...selectedValues, { label, value: val }];

    setSelectedValues(updated);
    onChange(updated.map((v) => v.value ?? v));
  };

  const handleSingleSelect = (item) => {
    const val = typeof item === "object" ? item : { label: item, value: item };
    onChange(val.value ?? val);
    setShowDropDown(false);
    setSearched("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (isMulti) {
      setSelectedValues([]);
      onChange([]);
    } else {
      onChange("");
    }
    setSearched("");
    if (typeof onClear === "function") {
      onClear();
    }
  };

  const displayValue = () => {
    if (!value || (Array.isArray(value) && value.length === 0)) return "";

    const getLabel = (val) => {
      if (typeof val === "object") return formatValue(val.label ?? val.value);
      const match = options.find((opt) => opt.value === val);
      if (match) return formatValue(match.label);
      return formatValue(val);
    };

    return isMulti ? value.map(getLabel).join(", ") : getLabel(value);
  };

  const clearSearch = () => {
    setSearched("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const hasValue = () => {
    if (isMulti) {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };
  return (
    <div className={className}>
      <Label label={label} name={name} required={required} />
      <OutsideClickHandler onOutsideClick={() => setShowDropDown(false)}>
        <div className={styles.dropdownInput}>
          <div
            className={`${styles.dropdownWrapper} ${
              error ? styles.error : ""
            } ${inputClassName} ${disabled ? styles.disabled : ""}`}
            onClick={() => {
              if (disabled) return;
              setShowDropDown((prev) => {
                const next = !prev;
                if (next && typeof onOpen === "function") onOpen();
                return next;
              });
            }}
            tabIndex={0}
          >
            <div
              className={`${styles.valueText} ${
                error ? styles.error : ""
              } ${inputClassName}`}
            >
              {isMulti ? (
                <div className={styles.selectedOptionsList}>
                  {selectedValues.length > 0 ? (
                    selectedValues.map((option, i) => (
                      <div
                        key={i}
                        className={styles.selectedOption}
                        title={formatValue(option)}
                      >
                        {formatValue(option)}
                        <span
                          className={styles.removeOption}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelection(option);
                          }}
                        >
                          ×
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className={styles.placeholder}>{placeholder}</span>
                  )}
                </div>
              ) : (
                <span className={styles.singleValue} title={displayValue()}>
                  {displayValue() || (
                    <span className={styles.placeholder}>{placeholder}</span>
                  )}
                </span>
              )}
            </div>
            {showClearButton && hasValue() && !disabled && (
              <span
                className={styles.clearButton}
                onClick={handleClear}
                title="Clear selection"
              >
                {clearButtonIcon}
              </span>
            )}

            <img
              alt=""
              src={showDropDown ? DropUpSvg : DropdownSvg}
              className={styles.arrowIcon}
            />
          </div>

          {showDropDown && (
            <div
              className={styles.dropdownBox}
              style={scrollbar === false ? { overflow: "auto" } : {}}
            >
              {isSearchable && (
                <div className={styles.searchBox}>
                  <Input
                    ref={inputRef}
                    placeholder="Search..."
                    value={searched}
                    onChange={(e) => setSearched(e.target.value)}
                    inputClassName={styles.searchInput}
                  />
                  {searched && (
                    <span
                      className={styles.clearSearch}
                      onClick={clearSearch}
                      onMouseEnter={(e) => (e.target.style.color = "#666")}
                      onMouseLeave={(e) => (e.target.style.color = "#999")}
                    >
                      ×
                    </span>
                  )}
                </div>
              )}

              {loading ? (
                <div className={styles.valueOption}>
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ marginRight: "8px" }}
                  />
                </div>
              ) : options.length > 0 ? (
                options.map((item, index) => {
                  const val = typeof item === "object" ? item.value : item;
                  const label = typeof item === "object" ? item.label : item;

                  const isSelected = isMulti
                    ? selectedValues.some((v) => (v.value ?? v) === val)
                    : (value?.value ?? value) === val;

                  return (
                    <div
                      key={index}
                      className={`${styles.optionText} ${
                        isSelected ? styles.selected : ""
                      }`}
                      title={formatValue(label)}
                      onClick={() =>
                        isMulti
                          ? toggleSelection(item)
                          : handleSingleSelect(item)
                      }
                    >
                      {formatValue(label)}
                      {isMulti && isSelected && (
                        <span className={styles.checkmark}>✓</span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={styles.noData}>No Results Found</div>
              )}
            </div>
          )}
        </div>
      </OutsideClickHandler>
      {error && <ErrorLabel>{helperText}</ErrorLabel>}
    </div>
  );
};

export default DropDown;
